/**
 * Quantum Field Effect
 *
 * 量子フィールドの揺らぎ - 最も根源的なエフェクト
 * サイバーパンク・ネオンマゼンタカラーパレット
 */

import * as THREE from 'three';
import type { SystemObject } from '../types.js';

/**
 * Create quantum field particle system
 */
export function createQuantumField(
  scene: THREE.Scene,
  systemsRef: React.MutableRefObject<SystemObject[]>
): void {
  const count = 5000;
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const phases = new Float32Array(count);
  const energies = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    // 球状に配置、黄金角で均等分布
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const y = 1 - (i / (count - 1)) * 2;
    const radius = Math.sqrt(1 - y * y) * (400 + Math.random() * 800);
    const theta = goldenAngle * i;

    positions[i3] = Math.cos(theta) * radius;
    positions[i3 + 1] = y * 600;
    positions[i3 + 2] = Math.sin(theta) * radius - 200;

    // 🎨 サイバーパンク・ネオンマゼンタ
    const colorChoice = Math.random();
    let color: THREE.Color;
    if (colorChoice < 0.4) {
      // ホットピンク〜マゼンタ
      color = new THREE.Color().setHSL(0.9 + Math.random() * 0.05, 1.0, 0.6);
    } else if (colorChoice < 0.7) {
      // ディープマゼンタ〜パープル
      color = new THREE.Color().setHSL(0.83 + Math.random() * 0.07, 0.9, 0.55);
    } else if (colorChoice < 0.9) {
      // ネオンシアン（アクセント）
      color = new THREE.Color().setHSL(0.5 + Math.random() * 0.05, 1.0, 0.6);
    } else {
      // 純白（ハイライト）
      color = new THREE.Color().setHSL(0.9, 0.1, 0.9);
    }
    colors[i3] = color.r;
    colors[i3 + 1] = color.g;
    colors[i3 + 2] = color.b;

    phases[i] = Math.random() * Math.PI * 2;
    energies[i] = Math.random() * 0.5 + 0.5;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('phase', new THREE.BufferAttribute(phases, 1));
  geometry.setAttribute('energy', new THREE.BufferAttribute(energies, 1));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      beat: { value: 0 },
      mouse: { value: new THREE.Vector2() }
    },
    vertexShader: `
      attribute vec3 color;
      attribute float phase;
      attribute float energy;
      varying vec3 vColor;
      varying float vAlpha;
      varying float vEnergy;
      uniform float time;
      uniform float beat;
      uniform vec2 mouse;

      // Simplex noise function
      vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
      vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
      vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }

      void main() {
        vColor = color;
        vEnergy = energy;
        vec3 pos = position;

        // 量子揺らぎ
        float quantumNoise = sin(pos.x * 0.01 + time + phase) *
                            cos(pos.y * 0.01 + time * 1.3) *
                            sin(pos.z * 0.01 + time * 0.7);

        pos += normalize(pos) * quantumNoise * 30.0 * energy;

        // ビートに反応
        pos *= 1.0 + beat * 0.3 * energy;

        // マウスに引き寄せられる
        vec2 toMouse = mouse * 300.0 - pos.xy;
        float mouseDist = length(toMouse);
        pos.xy += toMouse * 0.1 * exp(-mouseDist * 0.003);

        // 不確定性
        float uncertainty = sin(time * 3.0 + phase) * 5.0;
        pos += vec3(uncertainty, uncertainty * 0.7, uncertainty * 0.5);

        vAlpha = 0.4 + energy * 0.4 + beat * 0.3;

        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        gl_PointSize = (2.0 + energy * 3.0 + beat * 5.0) * (600.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vAlpha;
      varying float vEnergy;

      void main() {
        float dist = length(gl_PointCoord - vec2(0.5));
        if (dist > 0.5) discard;

        // 量子的な輝き
        float core = exp(-dist * 8.0);
        float glow = exp(-dist * 3.0);
        float halo = exp(-dist * 1.5) * 0.3;

        vec3 finalColor = vColor * (core * 2.0 + glow + halo);
        finalColor += vec3(1.0) * core * 0.5; // 白いコア
        float alpha = (core + glow * 0.5 + halo * 0.3) * vAlpha;

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });

  const quantum = new THREE.Points(geometry, material) as SystemObject;
  quantum.userData.update = (ctx) => {
    material.uniforms.time.value = ctx.time;
    material.uniforms.beat.value = ctx.beat.intensity;
    material.uniforms.mouse.value.set(ctx.mouse.x, ctx.mouse.y);
    quantum.rotation.y = ctx.time * 0.02;
    quantum.rotation.x = Math.sin(ctx.time * 0.1) * 0.1;
  };

  scene.add(quantum);
  systemsRef.current.push(quantum);
}
